import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ALL_SERVICES } from '../constants/services';
import SEO from '../components/SEO/SEO';

// 地域別都道府県データ（コンポーネント外で定義）
const regions = {
  '北海道': ['北海道'],
  '東北': ['青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県'],
  '関東': ['東京都', '神奈川県', '埼玉県', '千葉県', '茨城県', '栃木県', '群馬県'],
  '中部': ['新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県', '静岡県', '愛知県'],
  '関西': ['三重県', '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県'],
  '中国': ['鳥取県', '島根県', '岡山県', '広島県', '山口県'],
  '四国': ['徳島県', '香川県', '愛媛県', '高知県'],
  '九州': ['福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県']
};

const PartnerPage = () => {
  const navigate = useNavigate();
  const [selectedPrefecture, setSelectedPrefecture] = useState('佐賀県');
  const [selectedRegion, setSelectedRegion] = useState('九州');
  const [partnerOffices, setPartnerOffices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeSection, setActiveSection] = useState('section-image');
  const modalRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const [isShowingContactForm, setIsShowingContactForm] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    inquiry: ''
  });
  const [logos, setLogos] = useState([]);
  const [logosLoading, setLogosLoading] = useState(true);

  // 全都道府県リスト
  const allPrefectures = Object.values(regions).flat();

  // 表示する都道府県リスト
  const displayPrefectures = regions[selectedRegion] || [];

  // 位置情報から都道府県を推定
  useEffect(() => {
    const detectLocation = () => {
      if (!navigator.geolocation) {
        console.log('位置情報がサポートされていません');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log(`現在地: 緯度${latitude}, 経度${longitude}`);

          // 緯度経度から都道府県を推定（簡易版）
          const prefecture = getPrefectureFromCoordinates(latitude, longitude);
          if (prefecture) {
            console.log(`推定された都道府県: ${prefecture}`);
            setSelectedPrefecture(prefecture);
            // その都道府県が属する地域も選択
            const region = Object.keys(regions).find(reg => regions[reg].includes(prefecture));
            if (region) {
              setSelectedRegion(region);
            }
          }
        },
        (error) => {
          console.log('位置情報の取得に失敗:', error.message);
          // エラーの場合はデフォルトのまま（佐賀県）
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 0
        }
      );
    };

    detectLocation();
  }, []);

  // 緯度経度から都道府県を推定する関数（簡易版）
  const getPrefectureFromCoordinates = (lat, lng) => {
    // 各都道府県のおおよその緯度経度範囲（中心点で判定）
    const prefectureRanges = {
      '北海道': { minLat: 41.5, maxLat: 45.5, minLng: 139.5, maxLng: 145.5 },
      '青森県': { minLat: 40.3, maxLat: 41.5, minLng: 139.5, maxLng: 141.5 },
      '岩手県': { minLat: 38.8, maxLat: 40.5, minLng: 140.5, maxLng: 142.0 },
      '宮城県': { minLat: 37.8, maxLat: 39.0, minLng: 140.3, maxLng: 141.5 },
      '秋田県': { minLat: 39.0, maxLat: 40.5, minLng: 139.5, maxLng: 141.0 },
      '山形県': { minLat: 37.7, maxLat: 39.0, minLng: 139.5, maxLng: 140.5 },
      '福島県': { minLat: 36.8, maxLat: 38.0, minLng: 139.3, maxLng: 141.0 },
      '茨城県': { minLat: 35.7, maxLat: 36.9, minLng: 139.7, maxLng: 140.9 },
      '栃木県': { minLat: 36.2, maxLat: 37.1, minLng: 139.3, maxLng: 140.3 },
      '群馬県': { minLat: 35.8, maxLat: 36.9, minLng: 138.4, maxLng: 139.5 },
      '埼玉県': { minLat: 35.6, maxLat: 36.3, minLng: 138.7, maxLng: 139.9 },
      '千葉県': { minLat: 34.9, maxLat: 35.9, minLng: 139.7, maxLng: 140.9 },
      '東京都': { minLat: 35.5, maxLat: 35.9, minLng: 139.0, maxLng: 139.9 },
      '神奈川県': { minLat: 35.0, maxLat: 35.6, minLng: 138.9, maxLng: 139.8 },
      '新潟県': { minLat: 36.8, maxLat: 38.5, minLng: 137.6, maxLng: 139.9 },
      '富山県': { minLat: 36.3, maxLat: 36.9, minLng: 136.8, maxLng: 137.7 },
      '石川県': { minLat: 36.0, maxLat: 37.5, minLng: 136.3, maxLng: 137.3 },
      '福井県': { minLat: 35.3, maxLat: 36.3, minLng: 135.8, maxLng: 136.8 },
      '山梨県': { minLat: 35.2, maxLat: 35.9, minLng: 138.2, maxLng: 139.2 },
      '長野県': { minLat: 35.0, maxLat: 37.0, minLng: 137.3, maxLng: 138.9 },
      '岐阜県': { minLat: 35.2, maxLat: 36.4, minLng: 136.5, maxLng: 137.8 },
      '静岡県': { minLat: 34.6, maxLat: 35.4, minLng: 137.5, maxLng: 139.2 },
      '愛知県': { minLat: 34.5, maxLat: 35.4, minLng: 136.7, maxLng: 137.8 },
      '三重県': { minLat: 33.7, maxLat: 35.0, minLng: 135.8, maxLng: 136.9 },
      '滋賀県': { minLat: 34.8, maxLat: 35.7, minLng: 135.8, maxLng: 136.5 },
      '京都府': { minLat: 34.7, maxLat: 35.8, minLng: 135.0, maxLng: 136.0 },
      '大阪府': { minLat: 34.3, maxLat: 34.9, minLng: 135.1, maxLng: 135.7 },
      '兵庫県': { minLat: 34.4, maxLat: 35.7, minLng: 134.2, maxLng: 135.4 },
      '奈良県': { minLat: 33.9, maxLat: 34.8, minLng: 135.6, maxLng: 136.2 },
      '和歌山県': { minLat: 33.4, maxLat: 34.4, minLng: 135.1, maxLng: 136.0 },
      '鳥取県': { minLat: 35.1, maxLat: 35.6, minLng: 133.2, maxLng: 134.3 },
      '島根県': { minLat: 34.3, maxLat: 35.5, minLng: 131.7, maxLng: 133.5 },
      '岡山県': { minLat: 34.3, maxLat: 35.3, minLng: 133.3, maxLng: 134.4 },
      '広島県': { minLat: 34.0, maxLat: 34.9, minLng: 132.0, maxLng: 133.5 },
      '山口県': { minLat: 33.7, maxLat: 34.6, minLng: 130.8, maxLng: 132.3 },
      '徳島県': { minLat: 33.6, maxLat: 34.2, minLng: 133.7, maxLng: 134.8 },
      '香川県': { minLat: 34.0, maxLat: 34.5, minLng: 133.5, maxLng: 134.5 },
      '愛媛県': { minLat: 33.0, maxLat: 34.2, minLng: 132.4, maxLng: 133.3 },
      '高知県': { minLat: 32.7, maxLat: 33.9, minLng: 132.5, maxLng: 134.3 },
      '福岡県': { minLat: 33.3, maxLat: 33.9, minLng: 130.2, maxLng: 131.2 },
      '佐賀県': { minLat: 33.0, maxLat: 33.5, minLng: 129.8, maxLng: 130.5 },
      '長崎県': { minLat: 32.6, maxLat: 33.5, minLng: 128.8, maxLng: 130.2 },
      '熊本県': { minLat: 32.1, maxLat: 33.3, minLng: 130.3, maxLng: 131.2 },
      '大分県': { minLat: 32.8, maxLat: 33.6, minLng: 130.8, maxLng: 131.9 },
      '宮崎県': { minLat: 31.4, maxLat: 32.9, minLng: 130.7, maxLng: 131.8 },
      '鹿児島県': { minLat: 30.2, maxLat: 32.1, minLng: 129.4, maxLng: 131.0 },
      '沖縄県': { minLat: 24.0, maxLat: 27.0, minLng: 123.0, maxLng: 128.5 },
    };

    // 緯度経度がどの都道府県の範囲に含まれるか判定
    for (const [prefecture, range] of Object.entries(prefectureRanges)) {
      if (lat >= range.minLat && lat <= range.maxLat &&
          lng >= range.minLng && lng <= range.maxLng) {
        return prefecture;
      }
    }

    return null; // 該当なし
  };

  // CMSからパートナー事務所データを取得
  useEffect(() => {
    const fetchPartnerOffices = async () => {
      try {
        setLoading(true);

        // JSONからデータを取得
        const res = await fetch('/data/partners.json?t=' + Date.now());
        const contents = await res.json();

        // 都道府県ごとにグループ化
        const groupedByPrefecture = {};

        if (Array.isArray(contents)) {
          contents.forEach((office) => {
            const prefecture = office.prefecture;
            if (!groupedByPrefecture[prefecture]) {
              groupedByPrefecture[prefecture] = [];
            }
            groupedByPrefecture[prefecture].push(office);
          });
        }

        // 各都道府県ごとにランダムにシャッフル
        Object.keys(groupedByPrefecture).forEach(prefecture => {
          groupedByPrefecture[prefecture] = groupedByPrefecture[prefecture].sort(() => Math.random() - 0.5);
        });

        setPartnerOffices(groupedByPrefecture);
      } catch (err) {
        setError('パートナー事務所情報の取得に失敗しました');
        console.error('Failed to fetch partner offices:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerOffices();
  }, []);

  // ロゴを読み込む
  useEffect(() => {
    const loadLogos = async () => {
      try {
        const res = await fetch('/data/logo-list.json?t=' + Date.now());
        const data = res.ok ? await res.json().catch(() => []) : [];
        const loadedLogos = (Array.isArray(data) ? data : []).map((item, i) => ({
          id: i,
          src: item.logoUrl || '',
          alt: item.name || '',
          type: item.logoUrl ? 'image' : 'text',
        }));
        setLogos(loadedLogos.sort(() => Math.random() - 0.5));
      } catch {
        setLogos([]);
      }
      setLogosLoading(false);
    };
    loadLogos();
  }, []);

  // モーダル操作ハンドラー
  const openModal = (office) => {
    console.log('=== デバッグ: 事務所データ全体 ===');
    console.log('office:', office);
    console.log('=== 画像データの詳細 ===');
    console.log('image:', office.image);
    console.log('imageの型:', typeof office.image);
    console.log('imageは配列か:', Array.isArray(office.image));
    if (office.image) {
      console.log('imageの長さ:', office.image.length);
      console.log('image[0]:', office.image[0]);
      if (office.image[0]) {
        console.log('image[0]の型:', typeof office.image[0]);
        console.log('image[0]のキー:', Object.keys(office.image[0]));
      }
    }
    console.log('=== 代表者写真の確認 ===');
    console.log('representativeImage:', office.representativeImage);
    setSelectedOffice(office);
    setCurrentImageIndex(0); // 画像インデックスをリセット
    setActiveSection('section-image'); // アクティブセクションをリセット
    setIsShowingContactForm(false); // フォーム表示をリセット
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // スクロールを無効化
  };

  const closeModal = () => {
    setSelectedOffice(null);
    setCurrentImageIndex(0);
    setIsShowingContactForm(false);
    setContactFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      inquiry: ''
    });
    setIsModalOpen(false);
    document.body.style.overflow = ''; // スクロールを有効化
  };

  // 問い合わせフォームを表示
  const showContactForm = () => {
    setIsShowingContactForm(true);
    if (modalRef.current) {
      modalRef.current.scrollTop = 0; // 先頭にスクロール
    }
  };

  // 詳細に戻る
  const backToDetail = () => {
    setIsShowingContactForm(false);
    setContactFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      inquiry: ''
    });
  };

  // フォーム入力ハンドラー
  const handleContactFormChange = (field, value) => {
    setContactFormData(prev => ({ ...prev, [field]: value }));
  };

  // フォーム送信
  const handleContactFormSubmit = async (e) => {
    e.preventDefault();

    // バリデーション
    if (!contactFormData.name || !contactFormData.email || !contactFormData.inquiry) {
      alert('必須項目を入力してください');
      return;
    }

    // 送信データ
    const submitData = {
      ...contactFormData,
      officeName: selectedOffice.name,
      officeId: selectedOffice.id,
      prefecture: selectedOffice.prefecture,
      timestamp: new Date().toISOString()
    };

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userType: 'client',
          purposeType: 'partner_inquiry',
          name: contactFormData.name,
          email: contactFormData.email,
          companyName: contactFormData.company,
          phone: contactFormData.phone,
          inquiry: `【問い合わせ先事務所】${selectedOffice.name}（${selectedOffice.prefecture}）\n\n${contactFormData.inquiry}`,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert('お問い合わせを受け付けました。');
      } else {
        alert('送信に失敗しました。時間をおいて再度お試しください。');
      }
    } catch {
      alert('送信に失敗しました。時間をおいて再度お試しください。');
    }
    closeModal();
  };

  // 画像ナビゲーション
  const nextImage = () => {
    if (selectedOffice && selectedOffice.image) {
      setCurrentImageIndex((prev) =>
        prev === selectedOffice.image.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedOffice && selectedOffice.image) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedOffice.image.length - 1 : prev - 1
      );
    }
  };

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) closeModal();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  // スクロール位置に応じてアクティブセクションを更新
  useEffect(() => {
    if (!isModalOpen || !modalRef.current) return;

    const handleScroll = () => {
      const sections = [
        'section-image',
        'section-representative',
        'section-message',
        'section-about',
        'section-services',
        'section-achievements',
        'section-contact'
      ];

      const modalElement = modalRef.current;

      // 最下部までスクロールしているかチェック
      const isAtBottom = modalElement.scrollHeight - modalElement.scrollTop <= modalElement.clientHeight + 50;

      if (isAtBottom) {
        // 最下部の場合は最後のセクション（お問い合わせ）をアクティブに
        setActiveSection('section-contact');
        return;
      }

      // 通常のセクション検知
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          // セクションが画面上部の200px以内にある場合、そのセクションをアクティブにする
          if (rect.top <= 200 && rect.bottom > 200) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    const modalElement = modalRef.current;
    if (modalElement) {
      modalElement.addEventListener('scroll', handleScroll);
      // 初回実行
      handleScroll();
      return () => modalElement.removeEventListener('scroll', handleScroll);
    }
  }, [isModalOpen]);

  // 都道府県選択ハンドラー
  const handlePrefectureClick = (prefecture) => {
    setSelectedPrefecture(prefecture);
    // その都道府県が属する地域を自動選択
    const region = Object.keys(regions).find(reg => regions[reg].includes(prefecture));
    if (region) {
      setSelectedRegion(region);
    }
  };

  // 地域選択ハンドラー
  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    // その地域の最初の県を選択
    if (regions[region] && regions[region].length > 0) {
      setSelectedPrefecture(regions[region][0]);
    }
  };

  // 横スクロール用のナビゲーション
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="パートナー社労士事務所一覧｜全国の社労士を検索"
        description="SrWithパートナー社労士事務所を都道府県別に検索。厳選された社労士事務所と連携し、全国どこでも高品質な労務管理サービスを提供。パートナー事務所の募集も行っています。"
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-pink-beige to-white">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-48 translate-x-48 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full translate-y-40 -translate-x-40 blur-2xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-32">
          <div className="text-center space-y-6 md:space-y-8">
            <h1 className="text-4xl md:text-6xl font-black text-brown leading-tight">
              パートナー社労士
            </h1>
            <p className="text-xl md:text-2xl text-gray-text max-w-3xl mx-auto">
              <span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>と共に<br className="md:hidden" />「いい会社を5万社つくる」<br className="md:hidden" />ビジョンを実現する
            </p>
          </div>
        </div>
      </section>

      {/* 企業向け価値提案セクション */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-brown mb-6">
              なぜ<span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span><br className="md:hidden" />パートナー事務所なのか
            </h2>
            <p className="text-lg md:text-xl text-gray-text max-w-3xl mx-auto">
              全国の優秀な社労士事務所と連携し、企業様に最高品質のサービスを提供します
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* 選ばれる理由1 */}
            <div className="bg-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-pink-beige/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🏆</span>
              </div>
              <h3 className="text-xl font-bold text-brown mb-4">厳選されたパートナー</h3>
              <p className="text-gray-text">
                豊富な実務経験と確かな専門知識を持つ社労士のみを厳選してパートナー認定
              </p>
            </div>

            {/* 選ばれる理由2 */}
            <div className="bg-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-pink-beige/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🛡️</span>
              </div>
              <h3 className="text-xl font-bold text-brown mb-4">統一された高品質サービス</h3>
              <p className="text-gray-text">
                17年の実務経験を基にした労務プラットフォームで、全国どこでも同じ高品質なサービス
              </p>
            </div>

            {/* 選ばれる理由3 */}
            <div className="bg-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-pink-beige/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-bold text-brown mb-4">迅速な対応力</h3>
              <p className="text-gray-text">
                地域密着だからこそ実現できる、スピーディーで柔軟な対応とサポート
              </p>
            </div>
          </div>

          {/* パートナー事務所ロゴ横スクロールセクション */}
          <div className="w-full py-8">
            {logosLoading ? (
              <div className="text-center">
                <p className="text-gray-text">ロゴを読み込み中...</p>
              </div>
            ) : (
              <div
                className="overflow-hidden rounded-xl py-6"
                style={{
                  background: 'linear-gradient(135deg, #fafafa, #f0f0f0)'
                }}
              >
                <div className="flex animate-scroll-fast md:animate-scroll">
                  {[...logos, ...logos].map((logo, i) => (
                    <div
                      key={`logo-${i}`}
                      className="flex-shrink-0 mx-4 bg-white rounded-lg shadow-sm p-2 w-48 h-20 flex items-center justify-center"
                    >
                      {logo.type === 'image' ? (
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          className="max-w-full max-h-full object-contain min-h-[2rem]"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <span
                          className="text-center font-bold leading-tight px-2"
                          style={{
                            fontSize: (logo.alt || '').length > 12 ? '11px' : '13px',
                            color: '#403737',
                            letterSpacing: '0.02em',
                          }}
                        >
                          {logo.alt}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 実績数字 */}
          <div className="bg-pink-beige/10 rounded-2xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-brown mb-4">
                SrWithを生んだ事務所の実績
              </h3>
              <p className="text-gray-text">
                SrWithは17年以上の実務経験を持つ社労士事務所から生まれました
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-black text-brown mb-2">47</div>
                <div className="text-sm md:text-base text-gray-text">都道府県対応<br />（拡大中）</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-black text-brown mb-2">150+</div>
                <div className="text-sm md:text-base text-gray-text">創設事務所<br />支援企業数</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-black text-brown mb-2">17+</div>
                <div className="text-sm md:text-base text-gray-text">年以上の<br />実務経験</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-black text-brown mb-2">24h</div>
                <div className="text-sm md:text-base text-gray-text">以内の<br />初回対応</div>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-brown font-medium">
                ※対応エリア随時拡大中。お住まいの地域にパートナー事務所がない場合もお気軽にご相談ください
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ハイブリッド選択セクション */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-brown mb-6">
              お住まいの地域からパートナー社労士を検索
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

            {/* 左側：選択パネル */}
            <div className="lg:col-span-2 flex flex-col gap-6">

              {/* 1. ドロップダウン選択 */}
              <div className="bg-white p-6 rounded-2xl shadow-lg flex-shrink-0">
                <h3 className="text-lg font-bold text-brown mb-4 text-center">都道府県</h3>
                <select
                  value={selectedPrefecture}
                  onChange={(e) => handlePrefectureClick(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:border-pink-beige focus:outline-none text-center"
                >
                  {allPrefectures.map(pref => (
                    <option key={pref} value={pref}>{pref}</option>
                  ))}
                </select>
              </div>

              {/* 2. 地域別選択 */}
              <div className="bg-white p-6 rounded-2xl shadow-lg flex-1">
                <h3 className="text-lg font-bold text-brown mb-4 text-center">地域</h3>
                <div className="space-y-2">
                  {Object.keys(regions).map((region) => (
                    <button
                      key={region}
                      onClick={() => handleRegionChange(region)}
                      className={`w-full p-2 text-center rounded-lg transition-colors duration-300 h-10 flex items-center justify-center ${
                        selectedRegion === region
                          ? 'bg-pink-beige text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* 中央：都道府県一覧 */}
            <div className="lg:col-span-2">
              <div className="bg-white p-6 rounded-2xl shadow-lg h-full">
                <h3 className="text-lg font-bold text-brown mb-4 text-center">
                  {selectedRegion}
                </h3>
                <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto overflow-x-hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {displayPrefectures.map((pref) => (
                    <button
                      key={pref}
                      onClick={() => handlePrefectureClick(pref)}
                      className={`p-2 text-sm rounded-lg text-center transition-all duration-300 ${
                        selectedPrefecture === pref
                          ? 'bg-pink-beige text-white shadow-lg transform scale-105'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:shadow-md'
                      }`}
                    >
                      {pref}
                    </button>
                  ))}
                </div>
                {displayPrefectures.length === 0 && (
                  <p className="text-gray-500 text-center py-8">
                    該当する都道府県が見つかりません
                  </p>
                )}
              </div>
            </div>

            {/* 右側：選択された都道府県の事務所一覧 */}
            <div className="lg:col-span-8">
              <div className="bg-white p-6 rounded-2xl shadow-lg h-full relative">
                <h3 className="text-lg font-bold text-brown mb-4 text-center">
                  {selectedPrefecture}のパートナー事務所
                </h3>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-beige mx-auto mb-4"></div>
                    <p className="text-gray-500">読み込み中...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-3xl text-red-500">⚠️</span>
                    </div>
                    <p className="text-red-500 mb-4">{error}</p>
                    <button 
                      onClick={() => window.location.reload()}
                      className="bg-pink-beige text-white px-4 py-2 rounded-lg hover:bg-opacity-90"
                    >
                      再読み込み
                    </button>
                  </div>
                ) : partnerOffices[selectedPrefecture] && partnerOffices[selectedPrefecture].length > 0 ? (
                  <>
                    {/* 左ボタン */}
                    <button
                      onClick={scrollLeft}
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-brown/80 hover:bg-brown text-white rounded-full w-10 h-10 flex items-center justify-center transition-all duration-300 shadow-lg"
                    >
                      ←
                    </button>

                    {/* 右ボタン */}
                    <button
                      onClick={scrollRight}
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-brown/80 hover:bg-brown text-white rounded-full w-10 h-10 flex items-center justify-center transition-all duration-300 shadow-lg"
                    >
                      →
                    </button>

                    <div
                      ref={scrollContainerRef}
                      className="flex gap-3 md:gap-6 overflow-x-auto overflow-y-hidden h-[540px] md:h-[600px] pb-4"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {partnerOffices[selectedPrefecture].map((office) => (
                      <div
                        key={office.id}
                        className="bg-pink-100 border-2 border-pink-200 rounded-xl p-3 md:p-5 hover:shadow-xl transition-all duration-300 cursor-pointer hover:border-pink-beige hover:bg-pink-200/50 relative flex-shrink-0 w-[300px] md:w-[450px] h-[500px] md:h-[560px]"
                        onClick={() => openModal(office)}
                      >
                        {/* 創設事務所バッジ - 右上（デスクトップのみ） */}
                        {office.isFounder && (
                          <div className="hidden md:block absolute top-3 right-3 z-10">
                            <span className="inline-block bg-brown text-white text-[10px] px-2 py-1 rounded-full font-medium">
                              創設事務所
                            </span>
                          </div>
                        )}

                        <div className="flex flex-col items-center text-center space-y-2 md:space-y-3 h-full">
                          {/* ロゴまたは事務所名 - 中央 */}
                          {office.logo?.url ? (
                            <img
                              src={office.logo.url}
                              alt={`${office.name}ロゴ`}
                              className="h-10 w-auto max-w-[200px] object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <h4 className={`text-base md:text-lg font-bold text-brown w-full ${office.logo?.url ? 'hidden' : 'block'}`}>
                            {office.name}
                          </h4>

                          {/* 代表者写真または事務所画像 - Philosophy風 */}
                          <div className="relative w-full aspect-[4/5] max-w-[150px] md:max-w-[200px] mx-auto">
                            {office.representativeImage?.url ? (
                              <>
                                <div className="absolute inset-0 bg-gradient-to-br from-pink-beige/20 to-transparent rounded-2xl transform rotate-1"></div>
                                <div className="relative bg-white rounded-2xl overflow-hidden shadow-xl h-full">
                                  <img
                                    src={office.representativeImage.url}
                                    alt={office.representative}
                                    className="w-full h-full object-cover"
                                    style={{ objectPosition: office.representativeImagePosition || 'center' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div className="hidden w-full h-full items-center justify-center bg-pink-beige">
                                    <span className="text-white font-bold text-4xl">
                                      {office.name.charAt(0)}
                                    </span>
                                  </div>

                                  {/* 情報オーバーレイ - 上部 */}
                                  {office.experience && (
                                    <div className="absolute top-2 right-2 bg-pink-beige rounded-lg px-2 py-1 text-white text-[10px] font-bold shadow-md">
                                      {office.experience}
                                    </div>
                                  )}
                                  {office.supportedCompanies && (
                                    <div className="absolute top-2 left-2 bg-white rounded-lg px-2 py-1 text-brown text-[10px] font-bold shadow-md">
                                      {office.supportedCompanies}
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : office.image?.[0]?.url ? (
                              <>
                                <div className="absolute inset-0 bg-gradient-to-br from-pink-beige/20 to-transparent rounded-2xl transform rotate-1"></div>
                                <div className="relative bg-white rounded-2xl overflow-hidden shadow-xl h-full">
                                  <img
                                    src={office.image[0].url}
                                    alt={office.name}
                                    className="w-full h-full object-cover"
                                    style={{ objectPosition: office.imagePosition || 'center' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div className="hidden w-full h-full items-center justify-center bg-pink-beige">
                                    <span className="text-white font-bold text-4xl">
                                      {office.name.charAt(0)}
                                    </span>
                                  </div>

                                  {/* 情報オーバーレイ - 上部 */}
                                  {office.experience && (
                                    <div className="absolute top-2 right-2 bg-pink-beige rounded-lg px-2 py-1 text-white text-[10px] font-bold shadow-md">
                                      {office.experience}
                                    </div>
                                  )}
                                  {office.supportedCompanies && (
                                    <div className="absolute top-2 left-2 bg-white rounded-lg px-2 py-1 text-brown text-[10px] font-bold shadow-md">
                                      {office.supportedCompanies}
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="absolute inset-0 bg-gradient-to-br from-pink-beige/20 to-transparent rounded-2xl transform rotate-1"></div>
                                <div className="relative bg-pink-beige rounded-2xl overflow-hidden shadow-xl h-full flex items-center justify-center">
                                  <span className="text-white font-bold text-4xl">
                                    {office.name.charAt(0)}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>

                          <div className="w-full flex-1 flex flex-col">
                            <div className="space-y-1 md:space-y-2">
                              <p className="text-xs text-gray-700"><span className="font-medium">代表:</span> {office.representative}</p>
                              <button className="bg-pink-beige text-white text-xs px-3 py-1 rounded-full font-medium">
                                📍 {office.address.split('市')[0]}市
                              </button>
                            </div>

                            {office.services && office.services.length > 0 && (
                              <div className="mt-2 md:mt-3 w-full">
                                <div className="grid grid-cols-3 gap-1">
                                  {office.services.map((service, index) => (
                                    <span key={index} className="inline-block bg-pink-dark text-white text-[9px] px-1.5 py-0.5 rounded font-medium shadow-sm text-center leading-tight">
                                      {service}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="flex-1"></div>

                            <div className="mt-2 md:mt-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation(); // カードクリックとの重複を防ぐ
                                  openModal(office);
                                }}
                                className="w-full bg-brown text-white py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-semibold hover:bg-pink-dark transition-all duration-300 shadow-md"
                              >
                                詳細を見る
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-3xl text-gray-400">📍</span>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-500 mb-2">
                      {selectedPrefecture}では準備中です
                    </h4>
                    <p className="text-gray-400 mb-6 text-sm">
                      現在パートナー事務所を募集しております
                    </p>
                    <button className="bg-pink-beige text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-all duration-300 text-sm">
                      パートナー登録のご相談
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* モーダル */}
      {isModalOpen && selectedOffice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div ref={modalRef} className="bg-white rounded-2xl w-full h-full max-w-[95vw] max-h-[95vh] overflow-y-auto">
            
            {/* モーダルヘッダー */}
            <div className="sticky top-0 bg-pink-beige border-b border-pink-dark px-4 md:px-6 py-3 md:py-4 rounded-t-2xl relative z-20 shadow-md">
              <h2 className="text-lg md:text-2xl font-bold text-white text-center">SrWithパートナー会社</h2>
              <button
                onClick={closeModal}
                className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors duration-300"
              >
                <span className="text-white text-xl">×</span>
              </button>
            </div>

            {/* スティッキーナビゲーション */}
            <div className="sticky top-[52px] md:top-16 bg-white border-b border-gray-200 px-3 md:px-6 py-2 md:py-3 z-10 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
                {/* 左側：事務所名ボタン */}
                <button
                  onClick={() => document.getElementById('section-image')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-bold text-white rounded-lg transition-colors whitespace-nowrap flex-shrink-0 w-full md:w-auto ${
                    activeSection === 'section-image' ? 'bg-pink-dark' : 'bg-pink-beige hover:bg-pink-dark'
                  }`}
                >
                  {selectedOffice.name}
                </button>

                {/* 右側：ナビゲーションボタン */}
                <div className="grid grid-cols-3 md:flex gap-1 md:gap-2 w-full md:w-auto">
                  <button
                    onClick={() => document.getElementById('section-representative')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className={`px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                      activeSection === 'section-representative'
                        ? 'bg-pink-dark text-white'
                        : 'text-brown hover:bg-pink-beige/20'
                    }`}
                  >
                    代表者
                  </button>
                  <button
                    onClick={() => document.getElementById('section-message')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className={`px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                      activeSection === 'section-message'
                        ? 'bg-pink-dark text-white'
                        : 'text-brown hover:bg-pink-beige/20'
                    }`}
                  >
                    メッセージ
                  </button>
                  <button
                    onClick={() => document.getElementById('section-about')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className={`px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                      activeSection === 'section-about'
                        ? 'bg-pink-dark text-white'
                        : 'text-brown hover:bg-pink-beige/20'
                    }`}
                  >
                    概要
                  </button>
                  <button
                    onClick={() => document.getElementById('section-services')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className={`px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                      activeSection === 'section-services'
                        ? 'bg-pink-dark text-white'
                        : 'text-brown hover:bg-pink-beige/20'
                    }`}
                  >
                    サービス
                  </button>
                  <button
                    onClick={() => document.getElementById('section-achievements')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className={`px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                      activeSection === 'section-achievements'
                        ? 'bg-pink-dark text-white'
                        : 'text-brown hover:bg-pink-beige/20'
                    }`}
                  >
                    実績
                  </button>
                  <button
                    onClick={() => document.getElementById('section-contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className={`px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                      activeSection === 'section-contact'
                        ? 'bg-pink-dark text-white'
                        : 'text-brown hover:bg-pink-beige/20'
                    }`}
                  >
                    お問い合わせ
                  </button>
                </div>
              </div>
            </div>

            {/* モーダルコンテンツ */}
            <div className="space-y-0">
              {isShowingContactForm ? (
                /* 問い合わせフォーム */
                <div className="py-6 px-4 md:py-8 md:px-6">
                  <div className="max-w-2xl mx-auto">
                    {/* 戻るボタン */}
                    <button
                      onClick={backToDetail}
                      className="mb-4 md:mb-6 text-brown hover:text-pink-dark transition-colors duration-300 flex items-center gap-2 text-sm md:text-base"
                    >
                      ← 詳細に戻る
                    </button>

                    {/* タイトル */}
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-brown mb-2 text-center">
                      {selectedOffice.name}への問い合わせ
                    </h3>
                    <p className="text-gray-text text-center mb-6 md:mb-8 text-sm md:text-base">
                      <span className="font-semibold text-white px-2 py-1 rounded text-sm" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>を通じて安心してご相談いただけます
                    </p>

                    {/* フォーム */}
                    <form onSubmit={handleContactFormSubmit} className="space-y-6">
                      {/* お名前 */}
                      <div>
                        <label className="block text-brown font-medium mb-2">
                          お名前 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={contactFormData.name}
                          onChange={(e) => handleContactFormChange('name', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:border-pink-beige focus:outline-none"
                          placeholder="山田 太郎"
                          required
                        />
                      </div>

                      {/* 会社名 */}
                      <div>
                        <label className="block text-brown font-medium mb-2">
                          会社名
                        </label>
                        <input
                          type="text"
                          value={contactFormData.company}
                          onChange={(e) => handleContactFormChange('company', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:border-pink-beige focus:outline-none"
                          placeholder="株式会社〇〇"
                        />
                      </div>

                      {/* メールアドレス */}
                      <div>
                        <label className="block text-brown font-medium mb-2">
                          メールアドレス <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={contactFormData.email}
                          onChange={(e) => handleContactFormChange('email', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:border-pink-beige focus:outline-none"
                          placeholder="example@company.com"
                          required
                        />
                      </div>

                      {/* 電話番号 */}
                      <div>
                        <label className="block text-brown font-medium mb-2">
                          電話番号
                        </label>
                        <input
                          type="tel"
                          value={contactFormData.phone}
                          onChange={(e) => handleContactFormChange('phone', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:border-pink-beige focus:outline-none"
                          placeholder="000-0000-0000"
                        />
                      </div>

                      {/* 相談内容 */}
                      <div>
                        <label className="block text-brown font-medium mb-2">
                          相談内容 <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={contactFormData.inquiry}
                          onChange={(e) => handleContactFormChange('inquiry', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:border-pink-beige focus:outline-none"
                          rows="6"
                          placeholder="ご相談内容をご記入ください"
                          required
                        />
                      </div>

                      {/* 送信ボタン */}
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={backToDetail}
                          className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-all duration-300"
                        >
                          キャンセル
                        </button>
                        <button
                          type="submit"
                          className="flex-1 bg-accent-red text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-all duration-300 shadow-lg"
                        >
                          送信する
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              ) : (
                /* 詳細表示（既存のコンテンツ） */
                <>

              {/* 事務所画像セクション */}
              <div id="section-image" className="w-full scroll-mt-32 py-6 px-4 md:py-8 md:px-6">
                <div className="relative aspect-video bg-gray-200 rounded-xl overflow-hidden w-full md:w-4/5 mx-auto">
                    {selectedOffice.image && selectedOffice.image.length > 0 ? (
                      <>
                        <img
                          src={selectedOffice.image[currentImageIndex].url}
                          alt={`${selectedOffice.name}の画像 ${currentImageIndex + 1}`}
                          className="w-full h-full object-cover"
                          style={{ objectPosition: selectedOffice.imagePosition || 'center' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden w-full h-full items-center justify-center text-gray-400 bg-gray-100">
                          事務所画像
                        </div>

                        {/* 画像が2枚以上ある場合はナビゲーションボタンを表示 */}
                        {selectedOffice.image.length > 1 && (
                          <>
                            {/* 前へボタン */}
                            <button
                              onClick={prevImage}
                              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition-all duration-300"
                            >
                              ←
                            </button>

                            {/* 次へボタン */}
                            <button
                              onClick={nextImage}
                              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition-all duration-300"
                            >
                              →
                            </button>

                            {/* インジケーター */}
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
                              {selectedOffice.image.map((_, index) => (
                                <button
                                  key={index}
                                  onClick={() => setCurrentImageIndex(index)}
                                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                    index === currentImageIndex
                                      ? 'bg-white w-6'
                                      : 'bg-white/50'
                                  }`}
                                />
                              ))}
                            </div>

                            {/* 画像カウンター */}
                            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                              {currentImageIndex + 1} / {selectedOffice.image.length}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100">
                        事務所画像
                      </div>
                    )}
                  </div>
              </div>

              {/* 代表者情報セクション - ホームページ風 */}
              <div id="section-representative" className="w-full bg-gradient-to-br from-pink-beige/10 to-white py-12 px-4 md:py-16 md:px-6 scroll-mt-32">
                <div className="max-w-4xl mx-auto text-center space-y-6">
                <div className="flex flex-col items-center space-y-6">
                  {/* 役職と名前 */}
                  <div>
                    <p className="text-gray-text text-base md:text-lg mb-2">代表</p>
                    <h3 className="text-2xl md:text-3xl font-bold text-brown mb-2">{selectedOffice.representative}</h3>
                  </div>

                  {/* 代表者写真 - Philosophy風の大きなデザイン */}
                  {selectedOffice.representativeImage && selectedOffice.representativeImage.url ? (
                    <div className="relative w-64 md:w-80 mx-auto">
                      <div className="aspect-[4/5] relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-pink-beige/20 to-transparent rounded-[2rem] transform rotate-2"></div>
                        <div className="relative bg-white rounded-[2rem] overflow-hidden shadow-2xl">
                          <img
                            src={selectedOffice.representativeImage.url}
                            alt={`${selectedOffice.representative}の写真`}
                            className="w-full h-full object-cover"
                            style={{ objectPosition: selectedOffice.representativeImagePosition || 'center' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden w-full h-full items-center justify-center text-gray-400 bg-gray-50">
                            <div className="text-center">
                              <div className="w-20 h-20 bg-pink-beige rounded-full flex items-center justify-center mx-auto mb-3">
                                <span className="text-white font-bold text-3xl">
                                  {selectedOffice.name.charAt(0)}
                                </span>
                              </div>
                              <p className="text-sm">代表者写真</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative w-64 md:w-80 mx-auto">
                      <div className="aspect-[4/5] relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-pink-beige/20 to-transparent rounded-[2rem] transform rotate-2"></div>
                        <div className="relative bg-pink-beige rounded-[2rem] overflow-hidden shadow-2xl flex items-center justify-center">
                          <span className="text-white font-bold text-4xl md:text-6xl">
                            {selectedOffice.name.charAt(0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedOffice.isFounder && (
                    <span className="inline-block bg-pink-beige text-white text-sm px-4 py-1 rounded-full mt-3">
                      SrWith株式会社 創設事務所
                    </span>
                  )}
                </div>

                {(selectedOffice.established || selectedOffice.experience || selectedOffice.supportedCompanies) && (
                  <div className="bg-white p-6 rounded-2xl shadow-lg inline-block">
                    <div className="flex gap-8">
                      {selectedOffice.established && (
                        <div>
                          <p className="text-gray-600 text-sm mb-1">設立年</p>
                          <p className="font-semibold text-brown text-lg">{selectedOffice.established}</p>
                        </div>
                      )}
                      {selectedOffice.experience && (
                        <div>
                          <p className="text-gray-600 text-sm mb-1">経験年数</p>
                          <p className="font-semibold text-brown text-lg">{selectedOffice.experience}</p>
                        </div>
                      )}
                      {selectedOffice.supportedCompanies && (
                        <div>
                          <p className="text-gray-600 text-sm mb-1">支援企業数</p>
                          <p className="font-semibold text-brown text-lg">{selectedOffice.supportedCompanies}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </div>
              </div>

              {/* 代表メッセージセクション */}
              <div id="section-message" className="w-full bg-white py-12 px-4 md:py-16 md:px-6 scroll-mt-32">
                <div className="max-w-4xl mx-auto text-center">
                  <h4 className="text-2xl md:text-3xl lg:text-4xl font-bold text-brown mb-6 md:mb-8">代表メッセージ</h4>
                  <div className="bg-pink-beige/10 p-6 md:p-8 lg:p-12 rounded-2xl">
                    <p className="text-gray-700 text-base md:text-lg lg:text-xl leading-relaxed">
                      {selectedOffice.message}
                    </p>
                  </div>
                </div>
              </div>

              {/* 事務所概要セクション */}
              <div id="section-about" className="w-full bg-gray-50 py-12 px-4 md:py-16 md:px-6 scroll-mt-32">
                <div className="max-w-4xl mx-auto text-center">
                  <h4 className="text-2xl md:text-3xl lg:text-4xl font-bold text-brown mb-6 md:mb-8">事務所概要</h4>
                  <p className="text-gray-700 text-base md:text-lg lg:text-xl leading-relaxed">{selectedOffice.description}</p>
                </div>
              </div>

              {/* 対応サービスセクション */}
              <div id="section-services" className="w-full bg-white py-12 px-4 md:py-16 md:px-6 scroll-mt-32">
                <div className="max-w-5xl mx-auto text-center">
                  <h4 className="text-2xl md:text-3xl lg:text-4xl font-bold text-brown mb-6 md:mb-8">対応サービス</h4>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {ALL_SERVICES.map((service, index) => {
                      const isAvailable = selectedOffice.services && selectedOffice.services.includes(service);
                      return (
                        <span
                          key={index}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                            isAvailable
                              ? 'bg-pink-dark text-white shadow-lg'
                              : 'bg-gray-100 text-gray-300 border border-gray-200'
                          }`}
                        >
                          {service}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 実績・強みセクション */}
              {selectedOffice.achievements && (
                <div id="section-achievements" className="w-full bg-gray-50 py-12 px-4 md:py-16 md:px-6 scroll-mt-32">
                  <div className="max-w-4xl mx-auto text-center">
                    <h4 className="text-2xl md:text-3xl lg:text-4xl font-bold text-brown mb-6 md:mb-8">実績・強み</h4>
                    <div className="flex flex-col items-center gap-4 md:gap-6">
                      {selectedOffice.achievements.split('\n').filter(a => a.trim()).map((achievement, index) => (
                        <div key={index} className="bg-white p-4 md:p-6 rounded-xl shadow-lg border-t-4 border-pink-beige transform hover:scale-105 transition-transform duration-300 text-center w-full max-w-2xl">
                          <span className="text-gray-700 text-base md:text-lg">{achievement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* CTAセクション */}
              <div id="section-contact" className="w-full bg-white py-12 px-4 md:py-16 md:px-6 scroll-mt-32">
                <div className="max-w-4xl mx-auto text-center">
                  <h4 className="text-2xl md:text-3xl lg:text-4xl font-bold text-brown mb-4 md:mb-6">まずはお気軽にご相談ください</h4>
                  <p className="text-base md:text-lg lg:text-xl text-gray-text mb-6 md:mb-8">
                    <span className="font-semibold text-white px-2 py-1 rounded text-sm" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>を通じて安心してご相談いただけます。
                  </p>
                  <button
                    onClick={showContactForm}
                    className="bg-accent-red text-white px-8 md:px-12 py-3 md:py-4 rounded-2xl text-lg md:text-xl font-semibold hover:bg-red-700 transition-all duration-300 transform hover:scale-105 shadow-2xl"
                  >
                    🤝 この事務所に相談する
                  </button>
                </div>
              </div>

              {/* 連絡先フッター */}
              <div className="w-full bg-pink-beige px-4 md:px-6 py-6 md:py-8">
                <div className="text-center space-y-3 md:space-y-4">
                  {/* 事務所情報 */}
                  <div className="text-white">
                    <p className="font-semibold text-base md:text-lg mb-3 md:mb-4">{selectedOffice.name}</p>
                  </div>

                  {/* 住所と電話番号を左右に配置 */}
                  <div className="flex justify-center gap-4 md:gap-8 text-white flex-wrap">
                    <div className="text-center">
                      {selectedOffice.postalCode && (
                        <p className="text-xs mb-1">〒{selectedOffice.postalCode}</p>
                      )}
                      <p className="text-sm">{selectedOffice.address}</p>
                    </div>
                    {selectedOffice.phone && (
                      <div className="text-center">
                        <p className="text-xs mb-1">電話番号</p>
                        <p className="text-sm font-semibold">{selectedOffice.phone}</p>
                      </div>
                    )}
                  </div>

                  {selectedOffice.website && (
                    <div className="pt-1">
                      <a
                        href={selectedOffice.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-white text-pink-dark hover:bg-brown hover:text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 shadow-md"
                      >
                        🌐 ウェブサイトを見る
                      </a>
                    </div>
                  )}

                  {/* 著作権表記 */}
                  <div className="border-t border-white/20 pt-4 mt-4">
                    <p className="text-white text-xs">
                      © 2025 SrWith株式会社. All rights reserved.
                    </p>
                  </div>
                </div>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* パートナー募集セクション */}
      <section className="py-12 md:py-16 lg:py-24 bg-pink-beige">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-brown mb-6 md:mb-8">
            全国の<br className="md:hidden" />パートナー事務所を募集中
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-brown mb-8 md:mb-12 max-w-2xl mx-auto">
            「いい会社を5万社つくる」<br />
            大きなビジョンを<br className="md:hidden" />一緒に実現しませんか？<br />
            まずは情報交換から始めましょう。
          </p>

          <div className="space-y-3 md:space-y-4">
            <button
              onClick={() => navigate('/contact')}
              className="bg-brown text-white px-8 md:px-12 py-3 md:py-4 rounded-2xl text-base md:text-lg font-semibold hover:bg-brown/90 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              パートナー事務所登録のお問い合わせ
            </button>
            <button
              onClick={() => navigate('/contact?purpose=partner')}
              className="bg-pink-beige text-white px-8 md:px-12 py-3 md:py-4 rounded-2xl text-base md:text-lg font-semibold hover:bg-brown transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              パートナー事務所に申し込む
            </button>
            <p className="text-brown text-xs md:text-sm">
              まずは情報交換から始めさせていただきます
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerPage;